import React, { useState } from 'react';
import CalendarPicker from './CalendarPicker';
import LoadingCenter from './LoadingCenter';
import EmptyState from './EmptyState';

// ── Types ─────────────────────────────────────────────────────────────────────

export type InlineColType =
  | 'text' | 'color' | 'emoji' | 'toggle'
  | 'date' | 'number' | 'select' | 'badge' | 'readonly';

export interface InlineColDef<T = any> {
  key: string;
  label: string;
  type: InlineColType;
  width?: number | string;
  options?: { value: string; label: string }[];
  render?: (row: T) => React.ReactNode;
  /** Default true */
  editable?: boolean;
  placeholder?: string;
}

export interface InlineTableProps<T = any> {
  columns: InlineColDef<T>[];
  rows: T[];
  getKey: (row: T) => string | number;
  onAdd?: (row: Partial<T>) => Promise<void>;
  onEdit?: (id: string | number, row: Partial<T>) => Promise<void>;
  onDelete?: (id: string | number) => Promise<void>;
  canDelete?: (row: T) => boolean;
  defaultValues?: Partial<T>;
  renderExpandedForm?: (
    form: Partial<T>,
    onChange: (key: string, value: any) => void,
    onSave: () => void,
    onCancel: () => void
  ) => React.ReactNode;
  addLabel?: string;
  emptyText?: string;
  loading?: boolean;
}

// ── Styles matching design ─────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
  letterSpacing: '.07em', color: 'var(--text-color-calendar-week)',
  padding: '7px 14px',
  borderBottom: '1px solid rgba(0,0,0,.07)',
  background: 'rgba(0,0,0,.02)',
  textAlign: 'left', whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '8px 14px',
  borderBottom: '1px solid rgba(0,0,0,.04)',
  fontSize: 12, color: 'var(--text-color)', verticalAlign: 'middle',
};

const CELL_INPUT: React.CSSProperties = {
  height: 26, border: '1px solid #2563eb', borderRadius: 5,
  background: 'var(--bg-input)', color: 'var(--text-color)',
  fontSize: 12, padding: '0 7px', fontFamily: 'inherit', outline: 'none',
  boxShadow: '0 0 0 3px rgba(37,99,235,.1)',
};

const BTN_ICON = (danger?: boolean): React.CSSProperties => ({
  width: 26, height: 26,
  border: '1px solid rgba(0,0,0,.09)',
  borderRadius: 6, background: 'transparent',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 12, cursor: 'pointer',
  color: danger ? 'var(--color-danger, #dc2626)' : 'var(--text-color-calendar)',
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function renderCell<T>(col: InlineColDef<T>, row: T): React.ReactNode {
  const val = (row as any)[col.key];
  if (col.render) return col.render(row);
  switch (col.type) {
    case 'color':
      return <div style={{ width: 14, height: 14, borderRadius: 4, background: val || '#ccc', border: '1px solid rgba(0,0,0,.08)', display: 'inline-block' }} />;
    case 'emoji':
      return <span style={{ fontSize: 18 }}>{val}</span>;
    case 'toggle':
      return (
        <div style={{ width: 32, height: 17, borderRadius: 9, background: val ? 'var(--color-success, #059669)' : '#d1d5db', position: 'relative', flexShrink: 0 }}>
          <div style={{ position: 'absolute', width: 13, height: 13, background: '#fff', borderRadius: '50%', top: 2, left: val ? 17 : 2, transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
        </div>
      );
    case 'date':
      return val ? String(val).slice(0, 10) : '—';
    case 'badge':
      return val ? <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'rgba(37,99,235,.1)', color: '#2563eb' }}>{val}</span> : null;
    default:
      return val != null ? String(val) : '—';
  }
}

function renderInput<T>(col: InlineColDef<T>, form: Partial<T>, onChange: (k: string, v: any) => void): React.ReactNode {
  const val = (form as any)[col.key];
  const editable = col.editable !== false;
  if (!editable || col.type === 'badge' || col.type === 'readonly') return renderCell(col, form as T);

  switch (col.type) {
    case 'color':
      return <input type="color" title={col.label} value={val || '#6366f1'} onChange={e => onChange(col.key, e.target.value)}
        style={{ width: 32, height: 26, border: '1px solid rgba(0,0,0,.12)', borderRadius: 5, padding: 2, cursor: 'pointer' }} />;
    case 'emoji':
      return <input type="text" value={val || ''} onChange={e => onChange(col.key, e.target.value)}
        placeholder="😀" title={col.label} style={{ ...CELL_INPUT, width: 54, textAlign: 'center', fontSize: 16 }} />;
    case 'toggle':
      return <input type="checkbox" title={col.label} checked={!!val} onChange={e => onChange(col.key, e.target.checked)}
        style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }} />;
    case 'date':
      return <CalendarPicker mode="single" variant="bare" output="string" value={val || ''} onChange={(v) => onChange(col.key, v)}
        triggerStyle={{ ...CELL_INPUT, width: col.width ? undefined : 140 }} />;
    case 'number':
      return <input type="number" value={val ?? ''} onChange={e => onChange(col.key, e.target.value === '' ? null : Number(e.target.value))}
        placeholder={col.placeholder} style={{ ...CELL_INPUT, width: col.width ? undefined : 80 }} />;
    case 'select':
      return (
        <select value={val ?? ''} onChange={e => onChange(col.key, e.target.value)}
          style={{ ...CELL_INPUT, width: col.width ? undefined : 120, padding: '0 6px' }}>
          <option value="">—</option>
          {(col.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    default:
      return <input type="text" value={val || ''} onChange={e => onChange(col.key, e.target.value)}
        placeholder={col.placeholder} autoFocus
        style={{ ...CELL_INPUT, width: col.width ?? 160, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }} />;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function InlineTable<T = any>({
  columns, rows, getKey,
  onAdd, onEdit, onDelete, canDelete,
  defaultValues, renderExpandedForm,
  addLabel = '＋', emptyText, loading,
}: InlineTableProps<T>) {
  const [editId, setEditId] = useState<string | number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<Partial<T>>({});
  const [saving, setSaving] = useState(false);

  const setField = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const openAdd = () => {
    setEditId(null);
    setForm(defaultValues ?? {});
    setIsAdding(true);
  };

  const openEdit = (row: T) => {
    setIsAdding(false);
    setEditId(getKey(row));
    setForm({ ...row });
  };

  const cancel = () => { setEditId(null); setIsAdding(false); setForm({}); };

  const saveAdd = async () => {
    if (!onAdd || saving) return;
    setSaving(true);
    try { await onAdd(form); cancel(); } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!onEdit || editId == null || saving) return;
    setSaving(true);
    try { await onEdit(editId, form); cancel(); } finally { setSaving(false); }
  };

  if (loading) return <LoadingCenter />;

  // Action buttons — confirm/cancel
  const Confirm = ({ onSave }: { onSave: () => void }) => (
    <div style={{ display: 'flex', gap: 4 }}>
      <button type="button" style={{ ...BTN_ICON(), color: 'var(--color-success, #059669)', fontSize: 13 }} onClick={onSave} disabled={saving}>✓</button>
      <button type="button" style={{ ...BTN_ICON(), color: 'var(--text-color-calendar-week)', fontSize: 13 }} onClick={cancel}>✕</button>
    </div>
  );

  const hasActions = !!(onEdit || onDelete);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ ...TH, width: col.width }}>{col.label}</th>
            ))}
            {/* Last th: actions + add button */}
            {(hasActions || onAdd) && (
              <th style={{ ...TH, width: 72, textAlign: 'right', paddingRight: 10 }}>
                {onAdd && (
                  <button
                    type="button"
                    title={addLabel}
                    onClick={openAdd}
                    disabled={isAdding || editId != null}
                    style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: '1.5px dashed rgba(37,99,235,.4)',
                      background: 'rgba(37,99,235,.06)', color: '#2563eb',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1, transition: 'background .15s, border-color .15s',
                      opacity: (isAdding || editId != null) ? 0.4 : 1,
                    }}
                  >＋</button>
                )}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const id = getKey(row);
            const isEdit = editId === id;

            // Expanded form (for complex rows like schedule templates)
            if (isEdit && renderExpandedForm) {
              return (
                <tr key={id} style={{ background: 'rgba(37,99,235,.03)' }}>
                  <td colSpan={columns.length + (hasActions ? 1 : 0)} style={{ ...TD, padding: '12px 14px' }}>
                    {renderExpandedForm(form, setField, saveEdit, cancel)}
                  </td>
                </tr>
              );
            }

            return (
              <tr key={id}
                style={{ background: isEdit ? 'rgba(37,99,235,.03)' : undefined }}
                onMouseEnter={e => { if (!isEdit) e.currentTarget.style.background = 'rgba(0,0,0,.02)'; }}
                onMouseLeave={e => { if (!isEdit) e.currentTarget.style.background = ''; }}
              >
                {columns.map(col => (
                  <td key={col.key} style={TD}>
                    {isEdit ? renderInput(col, form, setField) : renderCell(col, row)}
                  </td>
                ))}
                {(hasActions || onAdd) && (
                  <td style={{ ...TD, textAlign: 'right' }}>
                    {isEdit ? (
                      <Confirm onSave={saveEdit} />
                    ) : (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {onEdit && <button type="button" style={BTN_ICON()} onClick={() => openEdit(row)} title="Edit">✏️</button>}
                        {onDelete && (
                          <button type="button" style={{ ...BTN_ICON(true), opacity: canDelete && !canDelete(row) ? 0.3 : 1 }}
                            onClick={() => onDelete(getKey(row))}
                            disabled={canDelete ? !canDelete(row) : false}
                            title="Delete">🗑</button>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}

          {/* Add row — inline */}
          {isAdding && !renderExpandedForm && (
            <tr style={{ background: 'rgba(37,99,235,.03)' }}>
              {columns.map(col => (
                <td key={col.key} style={{ ...TD, borderTop: '1px dashed rgba(37,99,235,.2)' }}>
                  {renderInput(col, form, setField)}
                </td>
              ))}
              {(hasActions || onAdd) && (
                <td style={{ ...TD, borderTop: '1px dashed rgba(37,99,235,.2)', textAlign: 'right' }}>
                  <Confirm onSave={saveAdd} />
                </td>
              )}
            </tr>
          )}

          {/* Expanded add form */}
          {isAdding && renderExpandedForm && (
            <tr style={{ background: 'rgba(37,99,235,.03)' }}>
              <td colSpan={columns.length + (hasActions ? 1 : 0)}
                style={{ ...TD, padding: '12px 14px', borderTop: '1px dashed rgba(37,99,235,.2)' }}>
                {renderExpandedForm(form, setField, saveAdd, cancel)}
              </td>
            </tr>
          )}

          {/* Empty state */}
          {rows.length === 0 && !isAdding && (
            <tr>
              <td colSpan={columns.length + (hasActions ? 1 : 0)}>
                <EmptyState message={emptyText || 'No items yet'} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
